import { CommonModule } from '@angular/common'
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  ElementRef,
  HostListener,
  effect,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs'
import { InternationalizationService } from '../../../../../services/internationalization.service'
import { GiphyMediaItem, GiphyMediaType } from '../../../interfaces/chat.interface'
import { MessageChatService } from '../../../services/message-chat.service'

import 'emoji-picker-element'

type PickerTab = 'emoji' | 'gifs' | 'stickers'

interface EmojiClickDetail {
  unicode?: string
  emoji?: { unicode?: string }
}

@Component({
  selector: 'app-media-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './media-picker.component.html',
  styleUrl: './media-picker.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MediaPickerComponent {
  readonly isOpen = input.required<boolean>()

  readonly emojiSelected = output<string>()
  readonly mediaSelected = output<{ file: File; content?: string }>()
  readonly closed = output<void>()

  readonly activeTab = signal<PickerTab>('emoji')
  readonly searchText = signal('')
  readonly items = signal<GiphyMediaItem[]>([])
  readonly loading = signal(false)
  readonly error = signal(false)

  private readonly hostRef = viewChild.required<ElementRef<HTMLElement>>('host')
  private readonly searchSubject = new Subject<{
    type: GiphyMediaType
    query: string
  }>()

  constructor(
    private readonly chatService: MessageChatService,
    protected translateService: InternationalizationService
  ) {
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged((a, b) => a.type === b.type && a.query === b.query),
        switchMap(({ type, query }) => {
          this.loading.set(true)
          this.error.set(false)
          return this.chatService.findGiphyMedia(type, query)
        }),
        takeUntilDestroyed()
      )
      .subscribe({
        next: (res) => {
          this.items.set(res.items)
          this.loading.set(false)
        },
        error: () => {
          this.items.set([])
          this.loading.set(false)
          this.error.set(true)
        },
      })

    effect(() => {
      if (this.isOpen()) {
        const tab = this.activeTab()
        if (tab !== 'emoji') {
          this.triggerSearch(tab, this.searchText())
        }
      }
    })
  }

  selectTab(tab: PickerTab): void {
    this.activeTab.set(tab)
    if (tab !== 'emoji') {
      this.triggerSearch(tab, this.searchText())
    }
  }

  onSearchChange(value: string): void {
    this.searchText.set(value)
    const tab = this.activeTab()
    if (tab === 'emoji') {
      return
    }
    this.triggerSearch(tab, value)
  }

  onEmojiClick(event: Event): void {
    const custom = event as CustomEvent<EmojiClickDetail>
    const unicode = custom.detail?.unicode ?? custom.detail?.emoji?.unicode ?? ''
    if (unicode) {
      this.emojiSelected.emit(unicode)
    }
  }

  async onMediaClick(item: GiphyMediaItem): Promise<void> {
    try {
      const response = await fetch(item.originalUrl)
      const blob = await response.blob()
      const ext = blob.type === 'image/webp' ? 'webp' : 'gif'
      const file = new File([blob], `giphy-${item.id}.${ext}`, {
        type: blob.type || 'image/gif',
      })
      this.mediaSelected.emit({ file })
      this.closed.emit()
    } catch {
      this.error.set(true)
    }
  }

  onClose(): void {
    this.closed.emit()
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen()) {
      return
    }
    const host = this.hostRef().nativeElement
    const target = event.target as Node | null
    if (target && !host.contains(target)) {
      const trigger = (target as HTMLElement).closest?.('.media-btn')
      if (trigger) {
        return
      }
      this.closed.emit()
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) {
      this.closed.emit()
    }
  }

  private triggerSearch(type: PickerTab, query: string): void {
    if (type === 'emoji') {
      return
    }
    this.searchSubject.next({ type, query })
  }
}
